"use client";

import { c as _c } from "react/compiler-runtime";
import { getConnectorLogoUrl } from "@/lib/connectorLogo";
import { cn } from "@/lib/utils";
import { formatConnectorName } from "@/utils/string";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

/**
 * Shared connector chip — used in template/integration detail "Integrate with" panels
 * and anywhere a compact app/logo badge is needed.
 *
 * Variants:
 *   - size: sm (h-7, 16px icon) / md (h-8, 20px icon) / lg (h-10, 24px icon)
 *   - variant: default (white card) / subtle (neutral-100 card)
 */
const pill = cva("inline-flex items-center gap-2 rounded-ds-sm border border-ds-border-default transition-colors", {
  variants: {
    variant: {
      default: "bg-ds-surface-card shadow-ds-soft",
      subtle: "bg-ds-surface-muted"
    },
    size: {
      sm: "h-7 py-0.5 pl-1 pr-2 text-xs",
      md: "h-8 py-1 pl-1 pr-2.5 text-2-5xs",
      lg: "h-10 py-1.5 pl-1.5 pr-3 text-sm"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "md"
  }
});
const iconSizes = {
  sm: "h-5 w-5",
  md: "h-6 w-6",
  lg: "h-7 w-7"
} as const;
function defaultDisplayName(connectorType: string): string {
  const brand = connectorType.includes(":") ? connectorType.split(":")[1] : connectorType;
  return formatConnectorName(brand);
}
export interface ConnectorPillProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">, VariantProps<typeof pill> {
  /** Connector type id, e.g. "slack", "telegram-bot", "oauth:gmail" */
  connectorType: string;
  /** Override the displayed name (defaults to formatted connectorType) */
  label?: string;
  /** Override the logo src (defaults to getConnectorLogoUrl(connectorType)) */
  logoUrl?: string;
}
export const ConnectorPill = React.forwardRef<HTMLDivElement, ConnectorPillProps>(function ConnectorPill(t0, ref) {
  const $ = _c(31);
  let className;
  let connectorType;
  let label;
  let logoUrl;
  let props;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      connectorType,
      label,
      logoUrl,
      variant,
      size: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = connectorType;
    $[3] = label;
    $[4] = logoUrl;
    $[5] = props;
    $[6] = t1;
    $[7] = variant;
  } else {
    className = $[1];
    connectorType = $[2];
    label = $[3];
    logoUrl = $[4];
    props = $[5];
    t1 = $[6];
    variant = $[7];
  }
  const size = t1 === undefined ? "md" : t1;
  let t2;
  if ($[8] !== connectorType || $[9] !== label) {
    t2 = label ?? defaultDisplayName(connectorType);
    $[8] = connectorType;
    $[9] = label;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const displayName = t2;
  let t3;
  if ($[11] !== connectorType || $[12] !== logoUrl) {
    t3 = logoUrl ?? getConnectorLogoUrl(connectorType);
    $[11] = connectorType;
    $[12] = logoUrl;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const src = t3;
  const iconClass = iconSizes[size ?? "md"];
  let t4;
  if ($[14] !== className || $[15] !== size || $[16] !== variant) {
    t4 = cn(pill({
      variant,
      size
    }), className);
    $[14] = className;
    $[15] = size;
    $[16] = variant;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== iconClass) {
    t5 = cn("rounded-ds-xs shrink-0 object-contain", iconClass);
    $[18] = iconClass;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== src || $[21] !== t5) {
    t6 = <img src={src} alt="" aria-hidden="true" className={t5} />;
    $[20] = src;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== displayName) {
    t7 = <span className="text-ds-text-primary font-medium leading-none">{displayName}</span>;
    $[23] = displayName;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== props || $[26] !== ref || $[27] !== t4 || $[28] !== t6 || $[29] !== t7) {
    t8 = <div ref={ref} className={t4} {...props}>{t6}{t7}</div>;
    $[25] = props;
    $[26] = ref;
    $[27] = t4;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
});